// biome-ignore-all lint/a11y/noNoninteractiveTabindex: Informational tooltip triggers must be keyboard focusable.
import type { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import { DraggableCard, DroppableSlot } from '@/components/shared/card-board'
import type { ScheduleDay, TrayCard } from '../_lib/auto-place'
import { formatHoursDuration, hoursToMinutes } from '../_lib/duration'
import { ScheduleCard, type ScheduleCardActions } from './schedule-card'

export interface DayColumnProps {
  day: ScheduleDay
  /** identifies the parent week so the slot id `${weekId}::${dayIdx}` can be built */
  weekId: string
  dayIdx: number
  usedHours: number
  readOnly: boolean
  placing: TrayCard | null
  onSetBudget: (budget: number) => void
  onPlace: () => void
  onOpenCustom: () => void
  cardActions: ScheduleCardActions
}

const SPECIAL_DAY_META: Record<
  Exclude<ScheduleDay['kind'], 'normal'>,
  {
    suffix: string
    labelColor: string
    background: string
    borderTop: string
  }
> = {
  blocked: {
    suffix: ' · BLOCKED',
    labelColor: 'var(--cd-red)',
    background: 'color-mix(in oklab, var(--cd-red) 4%, var(--cd-shell))',
    borderTop: '2px solid color-mix(in oklab, var(--cd-red) 40%, transparent)',
  },
  interview: {
    suffix: ' · INTERVIEW',
    labelColor: 'var(--cd-green)',
    background: 'color-mix(in oklab, var(--cd-green) 4%, var(--cd-shell))',
    borderTop:
      '2px solid color-mix(in oklab, var(--cd-green) 50%, transparent)',
  },
  avail: {
    suffix: ' · AVAIL.',
    labelColor: 'var(--cd-blue)',
    background: 'color-mix(in oklab, var(--cd-blue) 4%, var(--cd-shell))',
    borderTop:
      '2px dashed color-mix(in oklab, var(--cd-blue) 45%, transparent)',
  },
}

export function DayColumn(props: DayColumnProps): ReactNode {
  const {
    day,
    weekId,
    dayIdx,
    usedHours,
    readOnly,
    placing,
    onSetBudget,
    onPlace,
    onOpenCustom,
    cardActions,
  } = props
  const normal = day.kind === 'normal'
  const special = day.kind === 'normal' ? null : SPECIAL_DAY_META[day.kind]
  const label = `${day.label}${special?.suffix ?? ''}`
  const budget = day.budget ?? 0
  // Minutes, not hours — see `hoursToMinutes`. Summed card hours are a few
  // ULPs off, which a strict `>`/`===` turns into a red day at exactly budget.
  const usedMinutes = hoursToMinutes(usedHours)
  const budgetMinutes = hoursToMinutes(budget)
  const overBudget = normal && usedMinutes > budgetMinutes
  const exactlyFull = normal && usedMinutes === budgetMinutes
  const budgetColor = overBudget
    ? 'var(--cd-red)'
    : exactlyFull
      ? 'var(--cd-amber)'
      : 'var(--cd-text-faint)'
  const placementOverBudget = Boolean(
    normal &&
      placing &&
      usedMinutes + hoursToMinutes(placing.est) > budgetMinutes,
  )
  const canPlace = normal && !readOnly && placing !== null
  const canAddCustom = normal && !readOnly && placing === null
  // While a card is armed for placement, a normal day on a read-only week
  // (published or past) can't accept it — show a locked hint where the green
  // "place here" button would be so the day reads as intentionally locked
  // rather than silently unresponsive.
  const showLockedHint = normal && readOnly && placing !== null
  // Drop targets stay enabled for every normal, non-readOnly day regardless
  // of whether a card is armed (unlike `canPlace`, which also gates on
  // `placing`) — over-budget drops remain allowed, so budget plays no part.
  const canDropTarget = normal && !readOnly
  const slotId = `${weekId}::${dayIdx}`

  return (
    // The week grid is `gap-px` over a `--cd-border` background, so the border
    // colour is only ever meant to show through the 1px gaps. Grid stretches
    // this slot to the tallest column, but the day body inside it is only as
    // tall as its content — so in every shorter column the container's grey
    // showed through below the content as a band exactly as thick as the
    // height difference, which is why it appeared to flicker as cards were
    // added and removed. A flex column with a filling body leaves nothing to
    // show through. (`h-full` would be redundant: grid items already stretch.
    // `min-w-0` matches the shared board's other consumer so a nowrap child
    // cannot push the slot past its `minmax(0,1fr)` track.)
    <DroppableSlot
      id={slotId}
      disabled={!canDropTarget}
      className="flex min-w-0 flex-col"
    >
      <div
        role="group"
        aria-label={label}
        className="min-h-28 flex-1 p-2"
        style={{
          background: special?.background ?? 'var(--cd-shell)',
          borderTop: special?.borderTop ?? 'none',
        }}
      >
        <div className="mb-1.5 flex items-center gap-0.5">
          <span
            className="min-w-0 flex-1 text-[9.5px] font-bold"
            style={{ color: special?.labelColor ?? 'var(--cd-text-faint)' }}
          >
            {label}
          </span>
          {normal ? (
            <>
              {!readOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Reduce ${day.label} daily hours`}
                      onClick={() => onSetBudget(budget - 1)}
                      className="px-0.5 text-[9px] text-(--cd-text-ghost) hover:text-(--cd-text)"
                    >
                      −
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Reduce daily hours</TooltipContent>
                </Tooltip>
              ) : null}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    className="text-[9px] font-bold whitespace-nowrap"
                    style={{ color: budgetColor }}
                  >
                    {/* Budget is stepped in whole hours, so it renders as a
                        plain `Nh` while the used side gets the minute-grained
                        formatter. */}
                    {formatHoursDuration(usedHours)}/{budget}h
                  </span>
                </TooltipTrigger>
                <TooltipContent>hours used / daily budget</TooltipContent>
              </Tooltip>
              {!readOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Increase ${day.label} daily hours`}
                      onClick={() => onSetBudget(budget + 1)}
                      className="px-0.5 text-[9px] text-(--cd-text-ghost) hover:text-(--cd-text)"
                    >
                      +
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Increase daily hours</TooltipContent>
                </Tooltip>
              ) : null}
            </>
          ) : null}
        </div>

        {day.note ? (
          <p className="mb-1.5 text-[9.5px] leading-normal text-(--cd-text-faint)">
            {day.note}
          </p>
        ) : null}

        {day.cards.map((card) => (
          <DraggableCard
            key={card.id}
            id={card.id}
            data={{ kind: 'scheduled', card, weekId, dayIdx }}
            // Draggability tracks the card, not the week: a card is movable
            // unless it's completed or a fixed calendar event (or on a special,
            // non-normal day). Published/past weeks no longer freeze their
            // cards — the draft-only *persistence* still lives server-side and
            // is deferred to LG-347; until then a read-only drop resolves to
            // the existing scope-framed flash rather than a silent freeze.
            disabled={card.done || card.fixed || !normal}
          >
            <ScheduleCard card={card} readOnly={readOnly} {...cardActions} />
          </DraggableCard>
        ))}

        {canPlace ? (
          <button
            type="button"
            aria-label={`Place ${placing.title} on ${day.label}${placementOverBudget ? ' (over budget)' : ''}`}
            onClick={onPlace}
            className="w-full rounded-md border border-dashed border-(--cd-green)/55 bg-(--cd-green)/6 py-1.5 text-[9.5px] font-semibold text-(--cd-green-soft)"
          >
            {placementOverBudget ? 'place here (over budget)' : 'place here'}
          </button>
        ) : null}

        {showLockedHint ? (
          // No `title` tooltip here: it is hover-only (invisible on touch) and
          // the curriculum tooltip-compliance test forbids intrinsic titles.
          // The visible text plus the placement banner already carry the "pick
          // a draft week" message.
          <p className="w-full rounded-md border border-dashed border-(--cd-border-2) py-1.5 text-center text-[9px] font-semibold text-(--cd-text-ghost)">
            🔒 locked — pick a draft week
          </p>
        ) : null}

        {canAddCustom ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`Add a custom card to ${day.label} — any task, link, video, or external problem`}
                onClick={onOpenCustom}
                className="w-full rounded-md border border-dashed border-(--cd-border) py-1 text-[10px] font-bold text-(--cd-text-ghost) hover:border-(--cd-border-2) hover:text-(--cd-text-muted)"
              >
                ＋
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Add a custom card to this day — any task, link, video, or external
              problem
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </DroppableSlot>
  )
}
